import {
  Alert,
  Box,
  Button,
  Card,
  Modal,
  PageHeader,
  SegmentedControl,
  Tag,
  Text,
  Textarea,
} from '@tocktix/pantry';
import {
  DEFAULT_LOCALE,
  appLabelOf,
  appParam,
  parseApp,
  stateTagColor,
  typeLabel,
  typeSlug,
} from './legalConstants';
import {
  LegalPageType,
  PendingLegalPageReviewsDocument,
  useApproveLegalPageMutation,
  useLegalPageHistoryQuery,
  usePendingLegalPageReviewsQuery,
  useRejectLegalPageMutation,
} from 'generated/graphql';
import { diffLegalHtml } from 'utils/legalHtmlDiff';
import { hasEngRoleSelector } from 'selectors/rolesSelectors';
import { renderLegalContentHtml } from './renderLegalContentHtml';
import { sanitizeLegalHtml } from 'utils/legalHtmlSanitizer';
import { useActions } from 'utils/connect';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-v7';
import { useSelector } from 'react-redux';
import isLegalEditorSelector from 'selectors/isLegalEditorSelector';

const MIN_REJECTION_REASON_LENGTH = 10;

const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong.';

/** Approve / reject a single legal page identified by its review workflow id. */
const LegalReviewRoute: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { app: appParamRaw, workflowId } = useParams<{ app: string; workflowId: string }>();
  const app = parseApp(appParamRaw);
  const isLegalEditor = useSelector(isLegalEditorSelector);
  const isEng = useSelector(hasEngRoleSelector);
  const { actions } = useActions();
  const canReview = isEng || isLegalEditor;

  const [viewMode, setViewMode] = useState<'changes' | 'full'>('changes');
  const [actionTaken, setActionTaken] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const pendingQuery = usePendingLegalPageReviewsQuery({
    skip: !canReview,
    onError: () => actions.toast.showError("Couldn't load the review."),
  });
  const page =
    pendingQuery.data?.pendingLegalPageReviews.find(
      (candidate) => candidate.reviewWorkflowId === workflowId
    ) ?? null;

  // Pull the published row for this (type, app, locale) to diff against.
  const historyQuery = useLegalPageHistoryQuery({
    variables: {
      type: (page?.type ?? 'PRIVACY_POLICY') as LegalPageType,
      app: page?.app,
      locale: page?.locale,
    },
    skip: !page,
    onError: () =>
      actions.toast.showError("Couldn't load the published version to compare against."),
  });
  // Render both sides of the diff from the stored TipTap JSON (`content`) — the
  // same source readers render.
  const publishedRow =
    historyQuery.data?.legalPageHistory.find((candidate) => candidate.state === 'PUBLISHED') ??
    null;
  const publishedHtml = renderLegalContentHtml(publishedRow?.content) ?? '';
  const draftHtml = renderLegalContentHtml(page?.content) ?? '';

  const isLoading = pendingQuery.loading;

  const [approveLegalPage] = useApproveLegalPageMutation();
  const [rejectLegalPage] = useRejectLegalPageMutation();

  const hasBaseline = publishedHtml.trim().length > 0;
  const diffHtml = useMemo(
    () => diffLegalHtml(publishedHtml, draftHtml),
    [publishedHtml, draftHtml]
  );
  const fullHtml = useMemo(() => sanitizeLegalHtml(draftHtml), [draftHtml]);

  // The live page changed (or one appeared) since this draft was approved: the approver never saw
  // the current baseline, so the server will block the publish. Compare version ids (null-safe so
  // "nothing published at approval" and "nothing published now" read as unchanged).
  const staleBaseline =
    page?.state === 'APPROVED' &&
    (publishedRow?.id ?? null) !== (page?.approvedAgainstVersionId ?? null);

  // A draft byte-for-byte identical to the published version renders an empty redline, which reads
  // as "nothing happened". Detect it from the already-computed redline so the notice is exactly
  // consistent with what is (not) marked.
  const noChanges = useMemo(
    () => hasBaseline && !/class="diff-(ins|del|block)"/.test(diffHtml),
    [hasBaseline, diffHtml]
  );

  const handleApprove = useCallback(async () => {
    if (!page) {
      return;
    }
    try {
      await approveLegalPage({
        variables: { id: page.id },
        refetchQueries: [{ query: PendingLegalPageReviewsDocument }],
        awaitRefetchQueries: true,
      });
      actions.toast.showInfo('Approved - page is now awaiting publish.');
      setActionTaken(true);
    } catch (error) {
      actions.toast.showError(toErrorMessage(error));
    }
  }, [page, actions, approveLegalPage]);

  const handleRejectConfirm = useCallback(async () => {
    if (!page || rejectionReason.trim().length < MIN_REJECTION_REASON_LENGTH) {
      return;
    }
    try {
      await rejectLegalPage({
        variables: { id: page.id, reason: rejectionReason.trim() },
        refetchQueries: [{ query: PendingLegalPageReviewsDocument }],
        awaitRefetchQueries: true,
      });
      setRejectModalOpen(false);
      actions.toast.showInfo('Rejected - the author has been notified.');
      setActionTaken(true);
    } catch (error) {
      actions.toast.showError(toErrorMessage(error));
    }
  }, [page, rejectionReason, actions, rejectLegalPage]);

  if (!canReview) {
    return (
      <Box padding={[400, 500]}>
        <PageHeader title="Legal Page Review" />
        <Alert
          color="danger"
          message="You do not have access to review legal pages."
          data-testid="review-access-denied"
        />
      </Box>
    );
  }

  return (
    <Box padding={[400, 500]} display="flex" flexDirection="column" gap={300}>
      <PageHeader title="Legal Page Review" />
      <Box>
        <Button
          label="← Back to queue"
          variant="text"
          color="secondary"
          size="compact"
          onClick={() => navigate(`/legal/${appParam(app)}/reviews`)}
        />
      </Box>

      {!page && !isLoading && !actionTaken && (
        <Alert
          color="warning"
          message="Review request not found or already processed."
          data-testid="review-not-found"
        />
      )}

      {page && (
        <Box display="flex" flexDirection="column" gap={200}>
          <Card data-testid="review-meta">
            <Card.Body>
              <Box display="flex" flexDirection="column" gap={50}>
                <Box display="flex" alignItems="center" gap={100}>
                  <Text variant="bodyStrong">{typeLabel(page.type)}</Text>
                  <Tag label={appLabelOf(page)} color="primary" size="sm" />
                  {page.locale && <Tag label={page.locale} color="secondary" size="sm" />}
                  <Tag label={page.state} color={stateTagColor(page.state)} size="sm" />
                </Box>
                {page.createdBy && (
                  <Text variant="caption" color="fgSubtle">
                    Created by {page.createdBy.fullName ?? page.createdBy.email}
                    {page.createdBy.fullName && ` (${page.createdBy.email})`}
                    {page.createdAt && ` on ${new Date(page.createdAt).toLocaleString()}`}
                  </Text>
                )}
              </Box>
            </Card.Body>
          </Card>

          {actionTaken && (
            <Alert
              color="success"
              message="Done - the review queue has been updated."
              data-testid="review-action-success"
            />
          )}

          {page.state === 'APPROVED' && (
            <Alert
              color="primary"
              message="Approved - awaiting publish."
              data-testid="review-approved-readonly"
              actions={[
                {
                  actionVariant: 'link',
                  // `id` renders on the pantry Link's <a>, giving tests a stable hook.
                  id: 'go-to-publish-link',
                  label: 'Publish from the editor',
                  onClick: () =>
                    navigate(
                      `/legal/${appParam(app)}/${typeSlug(page.type)}?locale=${
                        page.locale ?? DEFAULT_LOCALE
                      }&draftId=${page.id}`
                    ),
                },
              ]}
            />
          )}

          {staleBaseline && (
            <Alert
              color="warning"
              message="The live page changed since this was approved - re-review recommended before it is published."
              data-testid="review-stale-baseline"
            />
          )}

          {noChanges && (
            <Alert
              color="primary"
              message="This draft is identical to the currently published version - there are no changes to review."
              data-testid="review-no-changes"
            />
          )}

          <Card data-testid="review-preview">
            <Card.Header border title="Content to review" />
            <Card.Body>
              <Box display="flex" flexDirection="column" gap={200}>
                {hasBaseline ? (
                  <SegmentedControl
                    id="legal-review-view-mode"
                    name="legal-review-view"
                    label="View"
                    currentValue={viewMode}
                    onChange={(event) =>
                      setViewMode(event.target.value === 'full' ? 'full' : 'changes')
                    }
                    data-testid="review-view-toggle"
                  >
                    <SegmentedControl.Option
                      id="legal-review-view-changes"
                      label="Show changes"
                      value="changes"
                    />
                    <SegmentedControl.Option
                      id="legal-review-view-full"
                      label="Full content"
                      value="full"
                    />
                  </SegmentedControl>
                ) : (
                  <Text variant="body" color="fgSubtle" data-testid="review-no-baseline">
                    New page — no published version to compare against.
                  </Text>
                )}
                <div
                  className="Legal"
                  data-testid="review-content"
                  dangerouslySetInnerHTML={{
                    __html: hasBaseline && viewMode === 'changes' ? diffHtml : fullHtml,
                  }}
                />
              </Box>
            </Card.Body>
          </Card>

          {!actionTaken && page.state !== 'APPROVED' && (
            <Box display="flex" gap={200} data-testid="review-actions">
              <Button
                label="Approve"
                color="success"
                data-testid="approve-btn"
                onClick={handleApprove}
              />
              <Button
                label="Reject"
                variant="outlined"
                color="danger"
                data-testid="reject-btn"
                onClick={() => setRejectModalOpen(true)}
              />
            </Box>
          )}
        </Box>
      )}

      <Modal
        id="legal-reject-modal"
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        size="md"
        data-testid="reject-dialog"
      >
        <Modal.Header title="Reject legal page" border />
        <Modal.Body>
          <Box display="flex" flexDirection="column" gap={200}>
            <Text variant="body" color="fgSubtle">
              Provide a clear reason so the author knows what to fix.
            </Text>
            <Textarea
              id="legal-rejection-reason"
              label="Rejection reason"
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              required
              fullWidth
              data-testid="rejection-reason-input"
              hint={`Minimum ${MIN_REJECTION_REASON_LENGTH} characters`}
            />
          </Box>
        </Modal.Body>
        <Modal.Footer
          actions={{
            primary: {
              label: 'Reject',
              danger: true,
              disabled: rejectionReason.trim().length < MIN_REJECTION_REASON_LENGTH,
              onClick: () => handleRejectConfirm(),
            },
            cancel: {
              label: 'Cancel',
              onClick: () => setRejectModalOpen(false),
            },
          }}
        />
      </Modal>
    </Box>
  );
};

export default LegalReviewRoute;
